import { AfterViewChecked, ChangeDetectionStrategy, Component, ElementRef, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Store } from '@ngrx/store';
import { Subscription, combineLatest } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AdBanner } from 'src/app/models/sidebar-widgets.model';
import { ConsentService } from 'src/app/services/consent.service';
import { SidebarWidgetsService } from 'src/app/services/sidebar-widgets.service';

const MAX_BANNERS = 2;

// Publicité sous « Deals les + hot » :
//  - bloc AdSense (identifiant réglé dans le back office, Settings → AdSense slot), seulement après accord
//    « Publicité » du bandeau cookies (le script AdSense est chargé par ConsentService) ;
//  - deux bannières d'affiliation au plus, de réseaux différents (Administration → Ads & news), tirées au hasard parmi
//    les actives. L'image vient de la copie en base de l'API : aucun appel au réseau avant le clic
//    (l'image d'origine n'est utilisée qu'avec l'accord « Publicité », tant que la copie n'existe pas).
@Component({
    selector: 'app-sidebar-ad',
    templateUrl: './sidebar-ad.component.html',
    styleUrls: ['./sidebar-ad.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SidebarAdComponent implements OnInit, AfterViewChecked, OnDestroy {

  @ViewChild('adsenseSlot') adsenseSlot?: ElementRef<HTMLElement>;

  readonly adsenseClient = environment.ADSENSE_CLIENT;
  adsenseSlotId = '';
  adsAllowed = false;
  banners: AdBanner[] = [];
  private allBanners: AdBanner[] = [];
  private pushedSlot: HTMLElement | null = null;
  private subscription?: Subscription;

  constructor(
    private store: Store<any>,
    private consentService: ConsentService,
    private widgets: SidebarWidgetsService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) { }

  get showAdsense(): boolean {
    return this.adsAllowed && !!this.adsenseClient && !!this.adsenseSlotId;
  }

  get hasContent(): boolean {
    return this.showAdsense || this.banners.length > 0;
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.subscription = combineLatest([
      this.store.select(state => state.SettingsStateFromModule?.settings?.[0]?.adsenseSlotSidebar),
      this.consentService.consent$,
      this.widgets.adBanners(),
    ]).subscribe(([slot, consent, banners]) => {
      this.adsenseSlotId = (slot || '').trim();
      const allowed = !!consent?.ads;
      if (allowed !== this.adsAllowed || banners !== this.allBanners) {
        this.adsAllowed = allowed;
        this.allBanners = banners || [];
        this.banners = this.pickBanners(this.allBanners);
      }
    });
  }

  // Le bloc <ins> n'existe qu'une fois affiché : AdSense le remplit au push (une fois par bloc)
  ngAfterViewChecked(): void {
    const slot = this.adsenseSlot?.nativeElement || null;
    if (slot && slot !== this.pushedSlot) {
      this.pushedSlot = slot;
      try {
        const w = window as any;
        (w.adsbygoogle = w.adsbygoogle || []).push({});
      } catch {
        // Bloqueur de publicité : le bloc reste vide
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  pictureOf(banner: AdBanner): string {
    return banner.storedPictureUrl || banner.pictureUrl;
  }

  // Copie en base inaccessible : image d'origine si le visiteur accepte la publicité, sinon bannière retirée
  onPictureError(banner: AdBanner, event: Event): void {
    const img = event.target as HTMLImageElement;
    if (this.adsAllowed && banner.pictureUrl && img.getAttribute('src') !== banner.pictureUrl) {
      img.src = banner.pictureUrl;
      return;
    }
    this.banners = this.banners.filter(b => b !== banner);
  }

  // Une bannière par réseau, au hasard parmi celles qui peuvent s'afficher, deux réseaux tirés au sort au plus
  private pickBanners(banners: AdBanner[]): AdBanner[] {
    const usable = banners.filter(b => b.storedPictureUrl || this.adsAllowed);
    const networks = [...new Set(usable.map(b => b.network))];
    const picked: AdBanner[] = [];
    while (networks.length > 0 && picked.length < MAX_BANNERS) {
      const [network] = networks.splice(Math.floor(Math.random() * networks.length), 1);
      const list = usable.filter(b => b.network === network);
      picked.push(list[Math.floor(Math.random() * list.length)]);
    }
    return picked;
  }
}
